import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Coding, Encounter, Questionnaire, QuestionnaireResponse } from "@medplum/fhirtypes";
import { CodingInput, useMedplum } from "@medplum/react";

import {
  Badge,
  Box,
  Button,
  Drawer,
  Grid,
  Group,
  Paper,
  Stack,
  Textarea,
  Title,
  useDrawersStack,
} from "@mantine/core";
import { FormErrors, useForm, UseFormReturnType } from "@mantine/form";

import { zodResolver } from "mantine-form-zod-resolver";

import { Loading } from "@/components/Loading";
import { TextWrapper } from "@/components/TextWrapper";
import { EncounterContext } from "@/contexts/encounter";
import { ProfileContext } from "@/contexts/profile";
import { DotPhraseRichTextField } from "@/dot-phrases";
import EncounterServiceDateAmendmentFields from "@/encounters/components/EncounterServiceDateAmendmentFields/EncounterServiceDateAmendmentFields";
import { DefaultEncounterDrawerFormsStack } from "@/encounters/constants/encounter-drawer-forms-stack";
import { EncounterNotesType } from "@/encounters/constants/encounter-notes-type";
import {
  EncounterChildSaveProvider,
  useEncounterChildSaveCoordinator,
} from "@/encounters/contexts/EncounterChildSaveContext";
import { useAmendmentWriteOnce } from "@/encounters/hooks/useAmendmentWriteOnce";
import useEncounterChargeCapture from "@/encounters/hooks/useEncounterChargeCapture";
import { updateAmendedEncounterDetails } from "@/encounters/services/amend-encounter-details";
import { fetchEncounterQuestionnaire } from "@/encounters/services/questionnaire";
import {
  createEncounterAnswerQuestionnaireResponse,
  fetchEncounterQuestionnaireResponse,
} from "@/encounters/services/questionnaire-response";
import { signAndLockAmendment } from "@/encounters/utils/amendment-sign-and-lock";
import { getSignAndLockValidationMessage } from "@/encounters/utils/form-error-messages";
import { getEncounterAnswersQuestionnaireResponse } from "@/encounters/utils/questionnaire-response";
import {
  EncounterAmendmentFormSchema,
  EncounterAmendmentFormType,
  EncounterAnswersForm,
  getEncounterInitialValues,
} from "@/encounters/zod-schemas/encounter-answers";
import { EncounterTypeOptions, Status } from "@/encounters/zod-schemas/encounters";
import { LOINCValueSets } from "@/fhir/enums/loinc";
import { Questionnaires } from "@/fhir/enums/questionnaires";
import { QuiltedHealthValueSets } from "@/fhir/enums/quilted-health";
import { Systems } from "@/fhir/enums/systems";
import EncounterServiceTypeVS from "@/fhir/seed-data/value-sets/qh-encounter-service-type.json";
import PhysicalExams from "@/physical-exams";
import { getCodeableConceptDisplay, joinReferenceString } from "@/utils/medplum";
import { toast } from "@/utils/notifications";
import { captureExceptionWithoutPhi } from "@/utils/sentry";

import DxClinicalTabs from "../DxPlan/DxClinicalTabs";
import { EncounterFooter } from "../EncounterForms/EncounterBase/EncounterFooter";
import { notifyClientOfAmendment } from "../EncounterPatientEmail/notifyClientOfAmendment";
import { EncounterObjectiveProcedures } from "../EncounterProcedures";
import { PrenatalFlowsheetSection } from "../PrenatalFlowsheet/components";
import EncounterAmendmentForm from "../shared/EncounterAmendmentForm";
import { FileProvider, Files, FilesDrawer } from "../shared/Files";
import { VitalSignsFormSection } from "../VitalSigns/components/VitalSignsFormSection";

const NEWBORN_IPP_FULL_NAME = "Newborn Immediate Postpartum";
const NEWBORN_IPP_TEMPLATE_NAME = "Newborn IPP";

const AmendEncounter = () => {
  const frontendUrl = import.meta.env.VITE_FRONTEND_URL;
  const medplum = useMedplum();
  const navigate = useNavigate();
  const { encounterId, clientId } = useParams() as { encounterId: string; clientId: string };
  const [isFetchingInitialData, setIsFetchingInitialData] = useState(true);
  const [isAmending, setIsAmending] = useState(false);
  const [questionnaireResponse, setQuestionnaireResponse] = useState<QuestionnaireResponse>();
  const [questionnaire, setQuestionnaire] = useState<Questionnaire>();
  const [encounter, setEncounter] = useState<Encounter>();
  const childSaveCoordinator = useEncounterChildSaveCoordinator();
  const writeAmendmentResponseOnce = useAmendmentWriteOnce<EncounterAmendmentFormType, void>();
  const writeEncounterDetailsOnce = useAmendmentWriteOnce<EncounterAmendmentFormType, void>();
  useEncounterChargeCapture({ coordinator: childSaveCoordinator, clientId, encounterId });
  const { profile } = useContext(ProfileContext);

  // Custom validator that skips amendment info validation for partial updates
  // Amendment info is only validated when signing and locking (in handleSubmit)
  const validateForm = (values: EncounterAmendmentFormType) => {
    // Use EncounterAnswersForm for partial updates (vitals, etc.)
    // This excludes encounterAmendmentInfo validation
    const result = EncounterAnswersForm.safeParse(values);
    if (!result.success) {
      return result.error.flatten().fieldErrors;
    }
    return {};
  };

  const form = useForm<EncounterAmendmentFormType>({
    initialValues: getEncounterInitialValues(questionnaireResponse, encounter),
    validate: validateForm,
  });

  const setFormValues = form.setValues;
  const setFormInitialValues = form.setInitialValues;

  // Keyed on the encounter id, so a save that only replaces the Encounter in context (such as time
  // spent) keeps the unsaved amendment edits in the form.
  useEffect(() => {
    if (questionnaireResponse && encounter) {
      const initialValues = getEncounterInitialValues(
        questionnaireResponse,
        encounter,
        form.values,
      );
      setFormValues(initialValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionnaireResponse, encounter?.id]);

  useEffect(() => {
    const fetchInitialData = async () => {
      const encounter = await medplum.readResource("Encounter", encounterId);

      if (!encounter) return;

      const encounterQuestionnaire = await fetchEncounterQuestionnaire(medplum, encounter);

      if (encounterQuestionnaire) {
        setQuestionnaire(encounterQuestionnaire);

        const encounterQuestionnaireResponse = await fetchEncounterQuestionnaireResponse({
          encounterId,
          questionnaireId: encounterQuestionnaire.id!,
          clientId,
          medplum,
        });

        setQuestionnaireResponse(encounterQuestionnaireResponse);
        const loadedValues = getEncounterInitialValues(encounterQuestionnaireResponse, encounter);
        // form.reset() restores these loaded values rather than the empty mount-time defaults.
        setFormInitialValues(loadedValues);
        setFormValues(loadedValues);
      }

      setEncounter(encounter);
      setIsFetchingInitialData(false);
    };
    fetchInitialData();
  }, [clientId, encounterId, medplum, setFormInitialValues, setFormValues]);

  /**
   * Validates and saves the amendment, then returns to the encounter.
   *
   * @param values The amendment form values.
   * @returns Whether the amendment was signed. `false` leaves the form as the provider left it.
   */
  const handleSubmit = async (values: EncounterAmendmentFormType): Promise<boolean> => {
    setIsAmending(true);
    // Validate amendment reason is required when signing and locking
    const reason = values.encounterAmendmentInfo?.encounterAmendmentInfoReason;
    const isReasonEmpty =
      !reason ||
      reason === "" ||
      (typeof reason === "object" && (!reason.code || reason.code === ""));

    if (isReasonEmpty) {
      form.setFieldError(
        "encounterAmendmentInfo.encounterAmendmentInfoReason",
        "Reason is required",
      );
      toast({
        title: "Validation Error",
        message: "Please select a reason for amendment.",
        type: "error",
      });
      setIsAmending(false);
      return false;
    }

    const validationErrors = zodResolver(EncounterAmendmentFormSchema)(values);
    if (Object.keys(validationErrors).length > 0) {
      form.setErrors(validationErrors);
      showSignAndLockValidationToast(validationErrors);
      setIsAmending(false);
      return false;
    }

    try {
      const parsedValues = EncounterAmendmentFormSchema.parse(values);

      if (!questionnaire) return false;

      // A retry after a later step failed reuses each write that already saved these values, so the
      // amendment history gets no duplicate version and the child saves link to the saved one.
      await writeAmendmentResponseOnce(parsedValues, async () => {
        // Save the main encounter QR FIRST so that physical exams can correctly
        // reference the new amendment's versionId when storing their parentVersionId.
        await createEncounterAnswerQuestionnaireResponse({
          form: parsedValues,
          clientId,
          medplum,
          encounterId,
          questionnaireResponse,
          practitionerReference: {
            reference: joinReferenceString("Practitioner", profile?.id ?? ""),
          },
          status: "completed",
          questionnaire,
        });
      });

      // Version diff for Type/Location/service date reads Encounter history, not QR alone.
      await writeEncounterDetailsOnce(parsedValues, async () => {
        const detailsResult = await updateAmendedEncounterDetails(
          medplum,
          encounterId,
          parsedValues.encounterDetails,
        );
        if (!detailsResult.ok) throw new Error("The amended encounter details were not saved");
      });

      medplum.invalidateSearches("QuestionnaireResponse");

      // Child resources must link to the newly-created main QR version.
      await childSaveCoordinator.saveAll({ status: "amended" });

      medplum.invalidateSearches("Encounter");

      // The client hears about the amendment only once every write above has succeeded.
      await notifyClientOfAmendment(
        {
          medplum,
          patientId: clientId,
          questionnaireResponse,
          encounterLink: `${frontendUrl}/clients/${clientId}/encounters/${encounterId}`,
          encounterNote: parsedValues.encounterClientSummary || "",
        },
        encounterId,
      );

      navigate(`/clients/${clientId}/encounters/${encounterId}`, {
        state: { statusChanged: true },
        preventScrollReset: true,
      });
      return true;
    } catch (error) {
      captureExceptionWithoutPhi("Failed to sign and lock an encounter amendment", error, {
        encounterId,
      });
      toast({
        title: "Failed to amend encounter",
        message:
          "The amendment could not be saved. Your changes are still on the page; try Sign & Lock again.",
        type: "error",
        autoClose: false,
        id: "amend-encounter-failed",
      });
      return false;
    } finally {
      setIsAmending(false);
    }
  };

  const handleUpdateLocalQuestionnaireResponse = async (values: EncounterAmendmentFormType) => {
    if (!questionnaire) return;
    setQuestionnaireResponse((prev) => ({
      ...prev,
      ...getEncounterAnswersQuestionnaireResponse({
        clientId,
        form: values,
        practitionerReference: {
          reference: joinReferenceString("Practitioner", profile?.id ?? ""),
        },
        questionnaire,
        status: "completed",
        encounterId,
        questionnaireResponse,
      }),
    }));
  };

  const template = getCodeableConceptDisplay(encounter?.serviceType)?.toLowerCase() || "";

  const isPrenatal = template.includes("prenatal");
  const isPrimaryCare = template.includes("primary care");
  const isGynecological = template.includes("gynecological");
  const isLabor = template.includes("labor");

  const isEncounterActive =
    encounter?.status !== Status.Finished && encounter?.status !== Status.Cancelled;

  // Use a single drawer stack that works for all encounter types
  const drawerStack = useDrawersStack([
    ...(isPrimaryCare || isPrenatal || isGynecological
      ? [DefaultEncounterDrawerFormsStack.FILES, DefaultEncounterDrawerFormsStack.CPT_CODE]
      : []),
  ]);

  const mainQuestionnaireUrl = isPrimaryCare
    ? Questionnaires.PrimaryCare
    : isGynecological
      ? Questionnaires.GynecologicalEncounter
      : Questionnaires.Prenatal;

  if ((!isFetchingInitialData && !encounterId && !encounter) || (encounter && isEncounterActive)) {
    navigate(`/clients/${clientId}/encounters/${encounterId}`, { preventScrollReset: true });
    return;
  }

  if (isFetchingInitialData) return <Loading />;

  return (
    <FileProvider medplum={medplum} clientId={clientId}>
      <EncounterContext.Provider value={{ encounter, setEncounter }}>
        <EncounterChildSaveProvider coordinator={childSaveCoordinator}>
          <Stack py="lg">
            <EncounterDetails
              form={form}
              encounter={encounter}
              onSubmit={handleSubmit}
              isSubmitting={isAmending}
              isFetchingInitialData={isFetchingInitialData}
            />
            <EncounterAmendmentForm form={form} />
            <EncounterAmendmentRichTextField
              form={form}
              title="Encounter Note"
              type={EncounterNotesType.ENCOUNTER_NOTE}
            />

            <DxClinicalTabs form={form} isAmendment isEncounterActive={isEncounterActive}>
              <Stack pt="xl" gap="xl">
                <EncounterAmendmentRichTextField
                  form={form}
                  title="Client Summary & Instructions"
                  type={EncounterNotesType.CLIENT_SUMMARY_NOTE}
                  subtitle="The client will be notified of any changes made"
                />
                {isPrenatal && (
                  <PrenatalFlowsheetSection
                    editable
                    form={form}
                    isAmendment
                    handleSubmit={async (values: EncounterAmendmentFormType) => {}}
                  />
                )}
                {!isLabor && (
                  <>
                    <VitalSignsFormSection
                      editable
                      form={form}
                      isAmendment
                      questionnaireResponse={questionnaireResponse}
                      isFetchingInitialData={isFetchingInitialData}
                      onSubmit={handleUpdateLocalQuestionnaireResponse}
                    />
                    <PhysicalExams isAmendment isEncounterActive submitTrigger={0} />
                    <EncounterObjectiveProcedures
                      isAmendment
                      submitTrigger={0}
                      encounter={encounter!}
                      open={drawerStack.open}
                      closeAll={drawerStack.closeAll}
                      register={drawerStack.register}
                      isEncounterActive={isEncounterActive}
                      mainQuestionnaireUrl={mainQuestionnaireUrl}
                      drawerName={DefaultEncounterDrawerFormsStack.CPT_CODE}
                    />
                    {(isPrimaryCare || isPrenatal || isGynecological) && (
                      <Files
                        isAmendment
                        isEncounterActive
                        medplum={medplum}
                        clientId={clientId}
                        open={drawerStack.open}
                        encounterId={encounterId}
                        filesDrawerId={DefaultEncounterDrawerFormsStack.FILES}
                      />
                    )}
                  </>
                )}
              </Stack>
            </DxClinicalTabs>
            {(isPrimaryCare || isPrenatal || isGynecological) && (
              <Drawer.Stack>
                <FilesDrawer
                  encounterId={encounterId}
                  register={drawerStack.register}
                  filesDrawerId={DefaultEncounterDrawerFormsStack.FILES}
                />
              </Drawer.Stack>
            )}
            <EncounterFooter
              form={form}
              encounter={encounter!}
              isSubmitting={isAmending}
              onSignAndLock={() => handleSubmit(form.values)}
            />
          </Stack>
        </EncounterChildSaveProvider>
      </EncounterContext.Provider>
    </FileProvider>
  );
};

export default AmendEncounter;

const EncounterDetails = ({
  form,
  encounter,
  isFetchingInitialData,
  onSubmit,
  isSubmitting = false,
}: {
  encounter?: Encounter;
  isFetchingInitialData: boolean;
  form: UseFormReturnType<EncounterAmendmentFormType>;
  onSubmit: (values: EncounterAmendmentFormType) => Promise<boolean>;
  isSubmitting?: boolean;
}) => {
  const template = getCodeableConceptDisplay(encounter?.serviceType);
  const [selectedTemplate, setSelectedTemplate] = useState(template || "");

  const handleTemplateChange = (value: Coding | undefined) => {
    if (value) {
      setSelectedTemplate(value.display!);
      form.setFieldValue("encounterDetails.encounterTemplate", {
        system: value.system!,
        code: value.code!,
        display: value.display!,
      });

      if (value.display === laborAndDelivery?.display && laborAndDelivery) {
        form.setFieldValue("encounterDetails.encounterType", {
          system: Systems.LOINC,
          code: laborAndDelivery.code ?? "",
          display: laborAndDelivery.display ?? "",
        });
      }
    }
  };

  const getTypes = () => {
    return EncounterTypeOptions[selectedTemplate as keyof typeof EncounterTypeOptions];
  };

  const laborAndDelivery = EncounterServiceTypeVS.compose.include
    .find((include) => include.system === Systems.LOINC)
    ?.concept?.find((concept) => concept.display.toLowerCase().includes("labor")) as
    | Coding
    | undefined;

  const onSignAndLock = () => signAndLockAmendment(form, () => onSubmit(form.values));

  return isFetchingInitialData ? (
    <Loading />
  ) : (
    <Paper p="lg" radius="lg">
      <Stack>
        <Group align="flex-start" justify="space-between">
          <Stack>
            <Group>
              <Title order={3}>
                {template === NEWBORN_IPP_TEMPLATE_NAME ? NEWBORN_IPP_FULL_NAME : template}{" "}
                Encounter
              </Title>
              <Badge c="red.7" tt="none" size="lg" bg="red.0" radius="sm">
                AMENDING
              </Badge>
            </Group>
          </Stack>
          <Button size="sm" disabled={isSubmitting} onClick={() => onSignAndLock()}>
            Sign & Lock
          </Button>
        </Group>
        <Grid justify="flex-start">
          <Grid.Col span={{ base: 12, xs: 6, sm: 3 }}>
            <CodingInput
              path=""
              disabled
              name="template"
              label="Template"
              creatable={false}
              clearable={false}
              withHelpText={false}
              binding={QuiltedHealthValueSets.EncounterServiceType}
              {...form.getInputProps("encounterDetails.encounterTemplate")}
              onChange={handleTemplateChange}
              defaultValue={
                encounter?.serviceType?.coding?.[0]?.code
                  ? encounter.serviceType?.coding?.[0]
                  : undefined
              }
            />
          </Grid.Col>
          {selectedTemplate !== "Primary Care" && (
            <Grid.Col span={{ base: 12, xs: 6, sm: 3 }}>
              {selectedTemplate !== "Other" ? (
                <Box data-testid="amend-encounter-type-input">
                  <CodingInput
                    path=""
                    name="type"
                    label="Type"
                    creatable={false}
                    clearable={false}
                    binding={getTypes()}
                    withHelpText={false}
                    disabled={selectedTemplate === laborAndDelivery?.display}
                    defaultValue={
                      encounter?.type?.[0]?.coding?.[0]?.code
                        ? encounter.type[0]?.coding?.[0]
                        : undefined
                    }
                    {...form.getInputProps("encounterDetails.encounterType")}
                  />
                </Box>
              ) : (
                <Textarea
                  rows={1}
                  label="Type"
                  autosize={true}
                  placeholder="New, Routine, Problem, FollowUp"
                />
              )}
            </Grid.Col>
          )}
          <Grid.Col span={{ base: 12, xs: 6, sm: 3 }}>
            <Box data-testid="amend-encounter-location-input">
              <CodingInput
                path=""
                name="location"
                label="Location"
                creatable={false}
                clearable={false}
                withHelpText={false}
                binding={LOINCValueSets.EncounterType}
                defaultValue={encounter?.class?.code ? encounter.class : undefined}
                {...form.getInputProps("encounterDetails.encounterLocation")}
              />
            </Box>
          </Grid.Col>
        </Grid>
        <EncounterServiceDateAmendmentFields form={form} />
      </Stack>
    </Paper>
  );
};

const EncounterAmendmentRichTextField = ({
  title,
  subtitle,
  form,
  type,
}: {
  title: string;
  subtitle?: string;
  form: UseFormReturnType<EncounterAmendmentFormType>;
  type: EncounterNotesType;
}) => {
  const noteTestId =
    type === EncounterNotesType.CLIENT_SUMMARY_NOTE
      ? "encounter-client-summary-editor"
      : "encounter-note";

  return (
    <Paper radius="lg">
      <Group
        align="center"
        justify="space-between"
        pb={{ base: 0, xs: "xs" }}
        mx={{ base: "xs", md: "xl" }}
        pt={{ base: "xs", xs: "md", sm: "lg", md: "xl" }}
      >
        <Stack gap={0}>
          <Title order={3} c="burgundy.9">
            {title}
          </Title>
          {subtitle && <TextWrapper size="sm">{subtitle}</TextWrapper>}
        </Stack>
      </Group>
      <DotPhraseRichTextField
        radius="lg"
        data-testid={noteTestId}
        px={{ base: "xs", md: "xl" }}
        pb={{ base: "xs", xs: "xl" }}
        {...form.getInputProps(type)}
      />
    </Paper>
  );
};

/**
 * Tells the provider which amendment fields block Sign & Lock.
 *
 * @param errors Validation errors from the amendment form.
 */
function showSignAndLockValidationToast(errors: FormErrors) {
  toast({
    title: "Validation Error",
    message: getSignAndLockValidationMessage(errors),
    type: "error",
  });
}
